import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { apiForgotPassword } from "@/lib/api";
import { testIds } from "@/constants/testIds/b2b";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await apiForgotPassword(email.trim());
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-card" data-testid="forgot-password-page">
      <div className="max-w-md mx-auto px-4 md:px-6 py-16">
        <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Wholesale portal</p>
        <h1 className="text-2xl md:text-3xl font-semibold mb-1">Reset your password</h1>

        {sent ? (
          <div
            data-testid={testIds.forgotSuccess}
            className="mt-6 p-5 rounded-md border border-border bg-secondary text-sm"
          >
            <p className="flex items-center gap-2 font-medium mb-1">
              <MailCheck size={16} strokeWidth={1.75} className="text-primary" /> Check your inbox
            </p>
            <p className="text-muted-foreground">
              If an account exists for <strong>{email.trim()}</strong>, we&apos;ve sent a link to reset your
              password. The link expires in 1 hour.
            </p>
            <Link
              to="/login"
              data-testid={testIds.forgotBackToLogin}
              className="inline-block mt-4 text-primary underline text-sm"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-8">
              Enter the email on your account and we&apos;ll send you a secure link to set a new password.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground block mb-1.5">Email</label>
                <input
                  data-testid={testIds.forgotEmail}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full px-3 py-2.5 text-sm border border-border rounded focus:border-primary"
                />
              </div>
              {err && <p className="text-sm text-destructive" data-testid={testIds.forgotError}>{err}</p>}
              <button
                data-testid={testIds.forgotSubmit}
                type="submit"
                disabled={busy}
                className="w-full py-2.5 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>
              <p className="text-sm text-center text-muted-foreground pt-2">
                Remembered it?{" "}
                <Link to="/login" className="text-primary underline" data-testid={testIds.forgotBackToLogin}>
                  Back to sign in
                </Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
